import React from 'react';
import { useMagnesiumOxideStore } from '../store/useMagnesiumOxideStore';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export function MagnesiumOxideChart() {
  const results = useMagnesiumOxideStore(s => s.results);
  const sorted = [...results].sort((a, b) => a.trialId - b.trialId);

  const data = {
    labels: sorted.map(r => `Trial ${r.trialId} (${r.ribbonLengthCm} cm)`),
    datasets: [
      {
        label: 'Mass of Mg (g)',
        data: sorted.map(r => r.mgMassG),
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
      },
      {
        label: 'Mass of O (g)',
        data: sorted.map(r => r.oxygenMassG),
        backgroundColor: 'rgba(245, 158, 11, 0.7)',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { stacked: true },
      y: { stacked: true, title: { display: true, text: 'Mass (g)' } },
    },
    plugins: {
      legend: { position: 'top' as const },
      title: { display: true, text: 'Mass of Mg and O combined in each trial' },
    },
  };

  const avgRatio = sorted.length > 0
    ? sorted.reduce((acc, r) => acc + r.ratioMgToO, 0) / sorted.length
    : 0;

  return (
    <div className="flex flex-col h-full">
      <h2 className="text-lg font-bold mb-3">Empirical Formula Chart</h2>
      {sorted.length === 0 && (
        <p className="text-sm text-muted-foreground">Run trials to see the chart.</p>
      )}
      {sorted.length > 0 && (
        <>
          <div className="flex-1 min-h-[300px]">
            <Bar data={data} options={options} />
          </div>
          <div className="mt-4 p-3 rounded-lg bg-card border border-border text-sm">
            <p className="text-muted-foreground">
              Across {sorted.length} trial{sorted.length === 1 ? '' : 's'}, the
              average mole ratio of Mg to O is
              <span className="font-mono font-bold text-foreground"> 1 : {avgRatio.toFixed(2)}</span>.
              The bars grow with the mass of Mg used, but the ratio of O to Mg
              stays roughly constant — the visual proof that the empirical
              formula does not depend on the starting amount of magnesium.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default MagnesiumOxideChart;
